/* eslint-disable indent */
/* eslint-disable no-shadow */
import React, { useState, useEffect, useRef } from 'react';
import ReactDragListView from 'react-drag-listview';

import { useDispatch, useSelector } from 'react-redux';
import screenerSlice, {
  screenerEffects,
  screenerSelectors,
} from 'features/screener/slice';
import {
  IColumnsScreenerSaveOrRunResult,
  IDataScreenerLoad,
} from 'features/screener/types';

import { emptyObject } from 'utils/general';
import handleErrorMessageAPI from 'global/AlertErrorMessage';
import ERROR_MESSAGE from 'constants/errorMessage';
import {
  Buttons,
  SButtonPageNavigation,
  SContainerNote,
  SContainerPageNavigation,
  SContainerTable,
  STable,
} from './styled';

import {
  addDeleteIcon,
  addSymbolColumnAndEmptySpace,
  directionsOption,
  setColumnOrder,
  moveArrayElements,
} from './utilities';

const ScreenerTable = () => {
  const [tableColumns, setTableColumns] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [tableAttributes, setTableAttributes] = useState<IDataScreenerLoad>({});
  const [addedColumns, setAddedColumns] = useState<Array<string>>([]);

  const latestTableAttributes = useRef<IDataScreenerLoad>({});

  latestTableAttributes.current = tableAttributes;

  const dispatch = useDispatch();

  const {
    screenerModal: { addColumnModal },
    screenerResult: { data: screenerResult },
    screenerPostResult: { data: screenerPostResult },
    screenerModal,
  } = useSelector(screenerSelectors);

  const dataScreenerResult = !emptyObject(screenerPostResult)
    ? screenerPostResult
    : screenerResult;

  const {
    screen_name: screenerName,
    screen_desc: screenerDesc,
    columns,
    order,
    sort,
    universe,
    rules,
    totalrows,
    curpage,
    perpage,
    sequence,
    screenerid,
    type,
  } = tableAttributes;

  const { postScreener } = screenerEffects;

  const onChangePagination = (page: number) => {
    dispatch(
      postScreener({
        name: screenerName,
        description: screenerDesc,
        ordercol: order,
        ordertype: sort,
        page,
        sequence: sequence.join(),
        universe,
        filters: JSON.stringify(rules),
        screenerid: `${screenerid}`,
        type,
      }),
    );
  };

  const validateFormScreener = () => {
    if (tableAttributes.rules.length === 0) {
      handleErrorMessageAPI(
        ERROR_MESSAGE.SCREENER_SPECIFY_RULES,
        ERROR_MESSAGE.ALERT_RED,
      );
      return false;
    }
    return true;
  };

  const onClickButtonNavigation = (action: string) => {
    if (validateFormScreener()) {
      if (action.toLowerCase() === 'first') {
        onChangePagination(1);
      }
      if (action.toLowerCase() === 'last') {
        const lastPage = Math.ceil(totalrows / perpage);
        onChangePagination(lastPage);
      }
      if (action.toLowerCase() === 'previous') {
        if (curpage !== 1) onChangePagination(curpage - 1);
      }

      if (action.toLowerCase() === 'next') {
        const isNextPageAvailable = curpage * perpage < totalrows;
        if (isNextPageAvailable) onChangePagination(curpage + 1);
      }
    }
  };

  const onCloseColumnHeader = (id: number) => {
    const { current } = latestTableAttributes;

    const {
      rules: filters,
      screen_name: name,
      screen_desc: description,
      universe: universes,
      curpage: page,
      order,
      sort: ordertype,
      sequence,
      columns,
    } = current;

    if (columns.length <= 1) {
      handleErrorMessageAPI(
        ERROR_MESSAGE.SCREENER_SPECIFY_RULES,
        ERROR_MESSAGE.ALERT_RED,
      );
      return;
    }

    const newRules = filters.filter((rule) => rule.item1 !== id);
    const newOrder =
      sequence.findIndex((s) => s === id.toString()) + 2 < order
        ? order - 1
        : order;
    const newSequence = sequence.filter((s) => s !== id.toString()).join();

    dispatch(
      postScreener({
        name,
        description,
        ordercol: newOrder,
        ordertype,
        sequence: newSequence,
        filters: JSON.stringify(newRules),
        universe: universes,
        page,
        screenerid: `${screenerid}`,
        type,
      }),
    );

    // Handle remove column in state
    const indexDeletedColumn = addedColumns.indexOf(id.toString());
    if (indexDeletedColumn !== -1) {
      const newAddedColumn = addedColumns.splice(indexDeletedColumn, 1);
      setAddedColumns(newAddedColumn);
    }
  };

  const columnStyleGenerator = (column) => {
    let className = 'custom-column';
    if (parseInt(column.removeable, 10)) {
      className += ' removeable';
    }
    if (parseInt(column.coloring, 10)) {
      className += ' coloring';
    }

    return className;
  };

  const createColumnTable = (
    columns: IColumnsScreenerSaveOrRunResult[],
    preferedOrder: number,
    preferedSort: string,
    sequence: IDataScreenerLoad['sequence'] = [],
  ) => {
    const sequencedColumns = sequence
      .map((x) => columns.find((y) => y.id === Number(x)))
      .filter((x) => x);

    const rawColumns = sequencedColumns.map((column, index) => ({
      title:
        column.removable === '1'
          ? addDeleteIcon({
              title: column.name,
              id: column.id,
              showDeleteIcon: true,
              // showDeleteIcon: addedColumns.includes(`${column.id}`),
              onCloseHeaderColumn: () => onCloseColumnHeader(column.id),
            })
          : column.name,
      className: columnStyleGenerator(column),
      dataIndex: ['results', `${index}`, 'display'],
      isRemovable: column.removable,
      sorter: true,
      coloring: column.coloring,
      width: '130px',
      key: column.id,
      onCell: (record) => ({ 'data-raw': record?.results[index].raw }),
    }));

    const compiledColumns = columns.length
      ? addSymbolColumnAndEmptySpace(rawColumns)
      : [];

    const columnsOrder = compiledColumns.map((column, index) => ({
      ...column,
      sortDirections: directionsOption(preferedSort),
      sortOrder: setColumnOrder(preferedOrder, index, preferedSort),
      columnSorterID: index + 1,
    }));

    return columnsOrder;
  };

  const onSortHandler = (orderData) => {
    const { order: nextOrderType, columnKey } = orderData;
    const orderCol = tableColumns.findIndex((col) => columnKey === col.key);

    dispatch(
      postScreener({
        name: screenerName,
        description: screenerDesc,
        ordercol: orderCol + 1,
        ordertype: nextOrderType === 'ascend' ? 'asc' : 'desc',
        filters: JSON.stringify(rules),
        universe,
        page: curpage,
        sequence: sequence.join(),
        save: '0',
        screenerid: `${screenerid}`,
        type,
      }),
    );

    dispatch(
      screenerSlice.actions.updateScreenerResult({
        screenerAction: 'get',
        screener: {
          order: orderCol + 1,
        },
      }),
    );
  };

  const generateTableColumn = (
    columns: IColumnsScreenerSaveOrRunResult[] = [],
    preferedOrder: number,
    preferedSort: string,
    sequence: IDataScreenerLoad['sequence'],
  ) => {
    const formatedTable = createColumnTable(
      columns,
      preferedOrder,
      preferedSort,
      sequence,
    );

    setTableColumns(formatedTable);
  };

  const generateTableData = (
    calcs: IDataScreenerLoad['calcs'] = [],
    sequence: IDataScreenerLoad['sequence'] = [],
  ) => {
    const sequencedCalcs = calcs.map((x) => ({
      ...x,
      results: sequence
        .map((seq) => x.results.find((y) => y.id === Number(seq)))
        .filter((x) => x),
    }));

    setTableData(sequencedCalcs);
  };

  const handleDragEnd = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0) return;

    const { calcs } = dataScreenerResult;

    const newCalcs = calcs.map((x) => ({
      ...x,
      results: moveArrayElements(x.results, fromIndex, toIndex),
    }));
    const newColumns = moveArrayElements(columns, fromIndex, toIndex);
    const newSequences = moveArrayElements(sequence, fromIndex, toIndex);
    const newRules = moveArrayElements(rules, fromIndex, toIndex);

    const fromIndexSeq = fromIndex + 2;
    const toIndexSeq = toIndex + 2;
    let newOrderCol = order;

    if (order === fromIndexSeq) newOrderCol = toIndexSeq;
    if (order > fromIndexSeq && order <= toIndexSeq) newOrderCol = order - 1;
    if (order < fromIndexSeq && order >= toIndexSeq) newOrderCol = order + 1;

    dispatch(
      screenerSlice.actions.updateScreenerResult({
        screenerAction: 'get',
        screener: {
          // order: newOrderCol,
          sequence: newSequences,
        },
      }),
    );

    if (!emptyObject(screenerPostResult)) {
      dispatch(
        screenerSlice.actions.updateScreenerResult({
          screenerAction: 'post',
          screener: {
            calcs: newCalcs,
            columns: newColumns,
            order: newOrderCol,
            sequence: newSequences,
            rules: newRules,
          },
        }),
      );
    }
  };

  // on modal add column closed
  const onModalClosed = (id: string) => {
    if (columns.find((x) => x.id === Number(id))) return;
    dispatch(
      postScreener({
        ordercol: order,
        ordertype: sort,
        page: curpage,
        universe,
        filters: JSON.stringify([
          ...(rules || []),
          {
            type: 'basic',
            item1: id,
            item2: '',
            operator: 'all',
            multiplier: '',
          },
        ]),
        name: screenerName,
        description: screenerDesc,
        sequence: [...sequence, id.toString()].join(),
        save: '0',
        screenerid: `${screenerid}`,
        type,
      }),
    );
    dispatch(screenerSlice.actions.resetAddColumnModal());
  };

  // listen when modal add column closed
  useEffect(() => {
    const { id } = addColumnModal;

    if (id) {
      onModalClosed(id);
    }
  }, [addColumnModal]);

  useEffect(() => {
    const {
      calcs: calcsResult,
      columns: columnsResult,
      order: orderResult,
      sort: sortResult,
      screenerid: screenerId,
      sequence = [],
      ...dataAttributes
    } = dataScreenerResult;

    const screenerIdValue = screenerId || screenerid;
    const uniqueSequence = sequence.filter((v, i) => sequence.indexOf(v) === i);

    generateTableColumn(columnsResult, orderResult, sortResult, uniqueSequence);
    generateTableData(calcsResult, uniqueSequence);
    setTableAttributes((prevState) => ({
      ...prevState,
      order: orderResult,
      sort: sortResult,
      columns: columnsResult,
      ...dataAttributes,
      sequence: uniqueSequence,
      screenerid: screenerIdValue,
    }));
  }, [dataScreenerResult]);

  useEffect(() => {
    // reset added column
    setAddedColumns([]);
  }, [screenerResult]);

  useEffect(() => {
    if (addColumnModal.id && rules) {
      const isColumnNeedUpdate = !rules.find(
        (rule) => rule.item1 === screenerModal.addColumnModal.id,
      );
      if (isColumnNeedUpdate) {
        setAddedColumns((prevState) => [
          ...prevState,
          `${screenerModal.addColumnModal.id}`,
        ]);
      }
    }
  }, [addColumnModal]);

  return (
    <>
      <SContainerPageNavigation>
        {totalrows !== tableData?.length &&
          Buttons.map((buttonLabel) => (
            <SButtonPageNavigation
              onClick={() => {
                onClickButtonNavigation(buttonLabel);
              }}
            >
              {buttonLabel}
            </SButtonPageNavigation>
          ))}
      </SContainerPageNavigation>
      <SContainerTable>
        <ReactDragListView.DragColumn
          ignoreSelector=".not-dragable"
          nodeSelector="th"
          onDragEnd={handleDragEnd}
        >
          <STable
            columns={tableColumns}
            dataSource={tableData}
            rowKey={(record: any) => record.company.id}
            pagination={false}
            showSorterTooltip={false}
            onChange={(pagination, filters, sorter) => {
              onSortHandler(sorter);
            }}
            // @ts-expect-error
            width="max-content"
            scroll={{ x: 1000 }}
          />
        </ReactDragListView.DragColumn>
      </SContainerTable>
      {totalrows !== tableData?.length && (
        <SContainerPageNavigation paddingBottom="5px">
          {Buttons.map((buttonLabel) => (
            <SButtonPageNavigation
              onClick={() => {
                onClickButtonNavigation(buttonLabel);
              }}
            >
              {buttonLabel}
            </SButtonPageNavigation>
          ))}
        </SContainerPageNavigation>
      )}
      <SContainerNote>
        *Market Data (Technical) di update setiap 15 menit. (Contoh: Price,
        Volume, Value)
        <br />
        *Fundamental Data di update setiap akhir hari pukul 06.30pm. (Contoh:
        Revenue, Net Profit)
      </SContainerNote>
    </>
  );
};

export default ScreenerTable;
